import {
  Datasheet,
  useDeviceDatasheetBuilder,
  ViewDeviceFooter,
} from 'features/Integrations/features/ConnectedDevices/features/Datasheet';
import {
  openStageSettings,
  openUSGSSettings,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { normalizeString } from 'utils/strings/string.utils';
import { isBuoyDevice } from '../../Buoy/utils/buoyDeviceUtils';
import { formatGraphTimeRangeLabel } from '../utils/graphHelpers.utils';

interface DeviceGraphDataSheetProps {
  device: Record<string, unknown>;
  startTime?: number;
  endTime?: number;
  onClose?: () => void;
  onSettingsOpen?: () => void;
  onViewDevice?: () => void;
}

/**
 * Renders inside the device graph when a device is selected but has no data
 * within the currently selected time range.
 *
 * Shows the selected time range in a banner, then renders a single-page
 * data sheet for the device's latest available reading.
 *
 * The data sheet component is resolved from `device.device_type` via a switch
 * (compared using `normalizeString`) so adding support for a new device type
 * only requires a new `case` here.
 */
const DeviceGraphDataSheet = ({
  device,
  startTime,
  endTime,
  onClose,
  onSettingsOpen,
  onViewDevice,
}: DeviceGraphDataSheetProps) => {
  const dispatch = useDispatch();

  const timeRangeLabel = useMemo(
    () => formatGraphTimeRangeLabel(startTime, endTime),
    [startTime, endTime],
  );

  // Slice to the single latest data point so the datasheet renders exactly
  // one page with no navigation controls.
  const singlePageDevice = useMemo(() => {
    if (!device) return device;
    const data = device.data;
    if (!Array.isArray(data) || data.length === 0) return device;
    const sorted = [...data].sort((a, b) => ((b.ts ?? 0) as number) - ((a.ts ?? 0) as number));
    return { ...device, data: [sorted[0]] };
  }, [device]);

  if (!device) return null;

  const handleClose = () => {
    dispatch(setSelectedDeviceId(null));
    onClose?.();
  };

  const handleSettingsOpen = () => {
    switch (normalizeString(device.device_type as string)) {
      case 'stage':
        dispatch(openStageSettings(device.id));
        break;
      case 'monitoring station':
        dispatch(openUSGSSettings(device.id));
        break;
    }
    onSettingsOpen?.();
  };

  const datasheetType = isBuoyDevice(device)
    ? 'buoy'
    : normalizeString(device.device_type as string) === 'monitoring station'
      ? 'usgs'
      : 'Flood';

  // The graph already fetched this device's range; the sheet shows the point
  // that was clicked, so it must not start a page fetch of its own.
  const datasheetProps = useDeviceDatasheetBuilder({
    type: datasheetType,
    device: singlePageDevice,
    fetchSeries: false,
    onClose: handleClose,
    onSettingsOpen: datasheetType === 'buoy' ? undefined : handleSettingsOpen,
    footer: <ViewDeviceFooter onViewDevice={onViewDevice} />,
  });

  return (
    <div className="connected-devices-graph-datasheet bg-primary h-100 rounded-3 overflow-hidden d-flex flex-column text-white">
      {/* No-data-in-range banner */}
      <div className="connected-devices-graph-datasheet__notice px-3 py-2 text-center small border-bottom border-theme-strong flex-shrink-0">
        <span className="text-theme-muted">No data in selected time range</span>
        {timeRangeLabel && (
          <>
            {': '}
            <span className="fw-semibold">{timeRangeLabel}</span>
          </>
        )}
      </div>
      <div className="flex-fill min-h-0 overflow-auto p-2">
        <Datasheet {...datasheetProps} />
      </div>
    </div>
  );
};

export default DeviceGraphDataSheet;
